import { useEffect, useState, useMemo, useRef } from "react";
import TicketCalculatorDropdown, { TicketCalculatorDropdownOption } from "./TicketCalculatorDropdown";
import TicketCalculatorSpeedometer, { TicketCalculatorSpeedometerRef } from "./TicketCalculatorSpeedometer";
import Swiper from "swiper";
import { data } from "../data";
import styles from "../styles.module.scss";

interface TicketCalculatorFormProps {
	updateSpeed: (speed: number) => void;
	updateSpeedLimit?: (speedLimit: { maxSpeed: number, minSpeed: number }) => void;
	updateFormData?: (formData: {
		vehicle: string;
		roadType: string;
		conditions: string[];
		speedLimit: number;
	}) => void;
	speedLimitOptions?: number[];
	defaultSpeedLimit?: number;
}

interface SpeedLimitProps {
	vehicle: TicketCalculatorDropdownOption[];
	roadType: TicketCalculatorDropdownOption[];
	conditions: TicketCalculatorDropdownOption[];
	minSpeed: number;
	maxSpeed: number;
	defaultSpeed: number;
}

/**
 * TicketCalculatorForm Component
 * 
 * Main form component for the ticket calculator that allows users to:
 * - Select vehicle type, road type, and driving conditions
 * - View speed limits and calculate potential violations
 * - Interact with a speedometer to set their speed
 * 
 * Features:
 * - Swiper-based dropdown navigation
 * - Dynamic speed limit calculation based on selections
 * - Automatic speedometer updates when defaults change
 */
const TicketCalculatorForm: React.FC<TicketCalculatorFormProps> = ({
	updateSpeed,
	updateSpeedLimit,
	updateFormData
}) => {
	const [selectedVehicle, setSelectedVehicle] = useState<TicketCalculatorDropdownOption[]>([]);
	const [selectedConditions, setSelectedConditions] = useState<TicketCalculatorDropdownOption[]>([]);
	const [selectedRoadType, setSelectedRoadType] = useState<TicketCalculatorDropdownOption[]>([]);
	const [filteredConditions, setFilteredConditions] = useState<TicketCalculatorDropdownOption[]>(data.conditions);
	const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
	const [currentSpeed, setCurrentSpeed] = useState<number>(0);
	const [speedLimit, setSpeedLimit] = useState<SpeedLimitProps>({
		vehicle: [],
		roadType: [],
		conditions: [],
		minSpeed: 0,
		maxSpeed: 0,
		defaultSpeed: 0
	});
	const [isSwiperReady, setIsSwiperReady] = useState(false);
	const dropdownsSwiper = useRef<HTMLDivElement | null>(null);
	const swiperInstance = useRef<Swiper | null>(null);
	const speedometerRef = useRef<TicketCalculatorSpeedometerRef>(null);

	/**
	 * Calculates the minimum speed variance required for red color indication
	 * @returns {number} The speed variance (30% of max speed)
	 */
	const speedStopRedMinVariance = useMemo(() => {
		return (speedLimit.defaultSpeed * 0.3);
	}, [speedLimit.defaultSpeed]);

	/**
	 * Generates speed stops for the speedometer color gradient
	 * Each stop defines a speed threshold and corresponding color/text
	 * @returns {Array} Array of speed stop objects with speed, color, and text
	 */
	const speedStops = useMemo(() => {
		return [
			{ speed: 0, color: "#33FF00", supplementalText: "Ingen fartoverskridelse", supplementalTextColor: "#000" },   // Bright green
			{ speed: speedLimit.defaultSpeed / 2, color: "#66FF00" },   // Green-yellow
			{ speed: speedLimit.defaultSpeed + 1, color: "#F2C94C", supplementalText: "Mindre fartoverskridelse", supplementalTextColor: "#000" },  // Yellow
			{ speed: (speedStopRedMinVariance / 2) + speedLimit.defaultSpeed, color: "#FF8C00" },  // Orange
			{ speed: speedStopRedMinVariance + speedLimit.defaultSpeed, color: "#EB5757", supplementalText: "Kraftig fartoverskridelse", supplementalTextColor: "#fff" },  // Red
			{ speed: 200, color: "#EB5757" }   // Red
		];
	}, [speedLimit.defaultSpeed]);

	/**
	 * Initializes and manages the Swiper instance for dropdown navigation
	 * Sets up responsive breakpoints and pagination
	 */
	useEffect(() => {
		// Use a small delay to ensure DOM is ready
		const timer = setTimeout(() => {
			if (dropdownsSwiper.current && !swiperInstance.current) {
				swiperInstance.current = new Swiper(dropdownsSwiper.current, {
					slidesPerView: 1,
					spaceBetween: 20,
					loop: false,
					pagination: {
						el: '.swiper-pagination',
						clickable: true,
						bulletClass: `${styles.swiperPaginationBullet} swiper-pagination-bullet`,
						bulletActiveClass: `${styles.swiperPaginationBulletActive} swiper-pagination-bullet-active`,
					},
					breakpoints: {
						768: {
							slidesPerView: 1,
							spaceBetween: 30,
						},
						1024: {
							slidesPerView: 3,
							spaceBetween: 40,
						}
					}
				});
				
				// Mark swiper as ready after initialization
				setIsSwiperReady(true);
			}
		}, 100);

		return () => {
			clearTimeout(timer);
			if (swiperInstance.current) {
				swiperInstance.current.destroy();
				swiperInstance.current = null;
				setIsSwiperReady(false);
			}
		};
	}, []);

	/**
	 * Filters available conditions based on selected vehicle
	 * Removes tempo 100 trailer option for vehicles that can't use it
	 */
	useEffect(() => {
		if (selectedVehicle.length > 0) {
			const vehicle = selectedVehicle[0].value;
			const vehicleCompatibility = data.vehicleTrailerCompatibility[vehicle as keyof typeof data.vehicleTrailerCompatibility];
			
			if (vehicleCompatibility) {
				const filtered = data.conditions.filter(condition => {
					// Always include non-trailer conditions
					if (condition.value !== "trailer" && condition.value !== "trailer-tempo100") {
						return true;
					}
					
					// Filter trailer options based on vehicle compatibility
					if (condition.value === "trailer") {
						return vehicleCompatibility.canUseTrailer;
					}
					
					if (condition.value === "trailer-tempo100") {
						return vehicleCompatibility.canUseTempo100Trailer;
					}
					
					return true;
				});
				
				setFilteredConditions(filtered);
				
				// Remove incompatible trailer selections
				const updatedConditions = selectedConditions.filter(condition => {
					if (condition.value === "trailer" && !vehicleCompatibility.canUseTrailer) {
						return false;
					}
					if (condition.value === "trailer-tempo100" && !vehicleCompatibility.canUseTempo100Trailer) {
						return false;
					}
					return true;
				});
				
				if (updatedConditions.length !== selectedConditions.length) {
					setSelectedConditions(updatedConditions);
				}
			}
		}
	}, [selectedVehicle]);

	/**
	 * Ensures only one trailer type can be selected at a time
	 * Implements alternation logic: if user selects a trailer type when another is already selected,
	 * it removes the previous selection and adds the new one
	 */
	useEffect(() => {
		const hasNormalTrailer = selectedConditions.find(condition => condition.value === "trailer");
		const hasTempo100Trailer = selectedConditions.find(condition => condition.value === "trailer-tempo100");
		
		if (hasNormalTrailer && hasTempo100Trailer) {
			// If both are selected, keep the most recently selected one
			// This will be handled by the dropdown's onChange logic
			// For now, we'll keep the tempo 100 trailer as it's more specific
			const updatedConditions = selectedConditions.filter(condition => condition.value !== "trailer");
			setSelectedConditions(updatedConditions);
		}
	}, [selectedConditions]);

	/**
	 * Updates speed limits when vehicle, road type, or conditions change
	 * Handles special cases like cars with trailers on specific road types
	 * Updates the speedLimit state with new min, max, and default speeds
	 */
	useEffect(() => {
		if (selectedVehicle.length > 0 && selectedRoadType.length > 0) {
			let vehicleSpeedLimit = null;
			const vehicle = selectedVehicle[0].value;
			const roadType = selectedRoadType[0].value;
			const hasNormalTrailer = selectedConditions.find((condition) => condition.value === "trailer");
			const hasTempo100Trailer = selectedConditions.find((condition) => condition.value === "trailer-tempo100");
			
			// Get the base speed limits for this vehicle and road type
			const baseSpeedLimit = (data.vehicleSpeedLimits[vehicle as keyof typeof data.vehicleSpeedLimits]?.[roadType as keyof typeof data.vehicleSpeedLimits.car] as any);
			
			// Check if vehicle can use tempo 100 trailers
			const canUseTempo100Trailer = data.vehicleTrailerCompatibility[vehicle as keyof typeof data.vehicleTrailerCompatibility]?.canUseTempo100Trailer;
			
			// Determine which trailer rules to apply
			if (hasTempo100Trailer && canUseTempo100Trailer) {
				// Use tempo 100 trailer rules
				vehicleSpeedLimit = baseSpeedLimit?.["trailer-tempo100"];
				// If no specific tempo 100 rules exist, fall back to base rules
				if (!vehicleSpeedLimit) {
					vehicleSpeedLimit = baseSpeedLimit;
				}
			} else if (hasNormalTrailer) {
				// Use normal trailer rules
				vehicleSpeedLimit = baseSpeedLimit?.trailer;
				// If no specific trailer rules exist, fall back to base rules
				if (!vehicleSpeedLimit) {
					vehicleSpeedLimit = baseSpeedLimit;
				}
			} else {
				// Use base speed limits (no trailer)
				vehicleSpeedLimit = baseSpeedLimit;
			}

			setSpeedLimit({
				vehicle: selectedVehicle,
				roadType: selectedRoadType,
				conditions: selectedConditions,
				minSpeed: vehicleSpeedLimit?.min || 0,
				maxSpeed: vehicleSpeedLimit?.default || 0,
				defaultSpeed: vehicleSpeedLimit?.default || 0
			});
		}
	}, [selectedVehicle, selectedRoadType, selectedConditions]);

	/**
	 * Automatically updates the speedometer when the default speed changes
	 * Only updates if user hasn't interacted with the speedometer
	 */
	useEffect(() => {
		if (speedometerRef.current) {
			const currentSpeed = speedometerRef.current.getValue();
			const newDefaultSpeed = speedLimit.defaultSpeed;
			
			// Only update if the new default is different from current speed
			// and if the user hasn't interacted with the speedometer
			if (currentSpeed !== newDefaultSpeed && !speedometerRef.current.hasUserInteracted()) {
				speedometerRef.current.setValueWithoutInteraction(newDefaultSpeed);
				setCurrentSpeed(newDefaultSpeed);
			}
		}
	}, [speedLimit]);

	/**
	 * Handles speed changes from the speedometer
	 */
	const handleSpeedChange = (speed: number) => {
		setCurrentSpeed(speed);
	};

	useEffect(() => {
		updateSpeed(currentSpeed);
	}, [currentSpeed]);

	// Update parent with speed limit information
	useEffect(() => {
		if (updateSpeedLimit && speedLimit.defaultSpeed > 0) {
			updateSpeedLimit({
				maxSpeed: speedLimit.defaultSpeed,
				minSpeed: speedLimit.minSpeed
			});
		}
	}, [speedLimit, updateSpeedLimit]);

	// Update parent with form data whenever form state changes
	useEffect(() => {
		if (updateFormData && selectedVehicle.length > 0 && selectedRoadType.length > 0) {
			updateFormData({
				vehicle: selectedVehicle[0].value,
				roadType: selectedRoadType[0].value,
				conditions: selectedConditions.map(condition => condition.value),
				speedLimit: speedLimit.defaultSpeed
			});
		}
	}, [selectedVehicle, selectedRoadType, selectedConditions, speedLimit.defaultSpeed, updateFormData]);

	return (
		<>

		<form className={styles.ticketcalculatorForm}>
			<div className={styles.ticketcalculatorFormSpeedometer}>
				<TicketCalculatorSpeedometer
					ref={speedometerRef}
					value={speedLimit.defaultSpeed}
					speedStops={speedStops}
					activeLabel={speedLimit.defaultSpeed.toString()}
					onChange={handleSpeedChange}
					onInput={handleSpeedChange}
				/>
			</div>
			<div className={styles.ticketcalculatorFormDropdowns}>
				<div className={`${styles.ticketcalculatorFormSlider} swiper ${isSwiperReady ? styles.swiperReady : styles.swiperNotReady}`} ref={dropdownsSwiper}>
				<div className="swiper-wrapper">
					<div className="swiper-slide">
						<TicketCalculatorDropdown
							type="single"
							label="Hvor kørte du?"
							options={data.roadTypes}
							selectedValue={selectedRoadType}
							setSelectedValue={setSelectedRoadType}
							dropdownId="roadType"
							openDropdownId={openDropdownId}
							setOpenDropdownId={setOpenDropdownId}
						/>
					</div>
					<div className="swiper-slide">
						<TicketCalculatorDropdown
							type="single"
							label="Hvad kørte du i?"
							options={data.vehicles}
							selectedValue={selectedVehicle}
							setSelectedValue={setSelectedVehicle}
							dropdownId="vehicle"
							openDropdownId={openDropdownId}
							setOpenDropdownId={setOpenDropdownId}
						/>
					</div>
					<div className="swiper-slide">
						<TicketCalculatorDropdown
							type="multiple"
							defaultText="Vælg en eller flere"
							label="Andre faktorer?"
							options={filteredConditions}
							selectedValue={selectedConditions}
							setSelectedValue={setSelectedConditions}
							dropdownId="conditions"
							openDropdownId={openDropdownId}
							setOpenDropdownId={setOpenDropdownId}
						/>
					</div>
				</div>
				
				{/* Pagination dots */}
				<div className={`${styles.swiperPagination} swiper-pagination`}></div>
			</div>
			</div>
		</form>
		</>
	);
};

export default TicketCalculatorForm;